<template>
  <div class="edit_vendor-container glass-tab-page">
    <div class="edit_vendor-header">
      <BrucdcumHeader :breadcrumb="['Vendor', 'Edit Vendor']" />
    </div>

    <div v-if="loading" class="edit_vendor-loading edit_vendor-loading-glass glass-tab-panel">
      <div class="edit_vendor-loading-spinner" aria-hidden="true"></div>
      <p>Loading vendor data...</p>
    </div>

    <form v-else @submit.prevent="handleSave" class="edit_vendor-form">
      <div class="glass-tab-view">
        <div class="glass-tab-view__tabs">
          <TabNavigation
            v-model="activeTab"
            :tabs="tabs"
            @update:modelValue="handleTabChange"
          />
        </div>

        <div class="glass-tab-view__content">
          <div class="glass-tab-view__panel" v-if="activeTab === 'general'">
            <div class="glass-tab-panel glass-tab-panel--flex-column glass-tab-panel--stagger-rows">
              <div class="edit_vendor-form-section">
                <div class="edit_vendor-form-group-row">
                  <div class="edit_vendor-form-group edit_vendor-form-group--label-input">
                    <LabelInputField
                      input-id="edit-vendor-id"
                      label="Vendor ID"
                      v-model="form.vendorId"
                      placeholder="Enter Vendor ID"
                      required
                      flex
                      :has-error="vendorIdExists || (showErrors && !form.vendorId)"
                      @input="handleVendorIdInput"
                    >
                      <div v-if="checkingVendorId" class="edit_vendor-checking-text">Checking availability...</div>
                      <div v-if="vendorIdExists" class="edit_vendor-error-text">This Vendor ID already exists in this company.</div>
                      <div v-if="showErrors && !form.vendorId" class="edit_vendor-error-text">Vendor ID is required.</div>
                    </LabelInputField>
                  </div>
                  <div class="edit_vendor-form-group edit_vendor-form-group--label-input">
                    <LabelInputField
                      input-id="edit-vendor-name"
                      label="Vendor Name"
                      v-model="form.vendorName"
                      placeholder="Enter Vendor Name"
                      required
                      flex
                      :has-error="showErrors && !form.vendorName"
                      @input="clearVendorNameError"
                    >
                      <div v-if="showErrors && !form.vendorName" class="edit_vendor-error-text">Vendor Name is required.</div>
                    </LabelInputField>
                  </div>
                </div>
                <div class="edit_vendor-form-group-row edit_vendor-form-group-row--field-pair">
                  <div class="edit_vendor-form-group edit_vendor-form-group--label-checkbox">
                    <LabelCheckboxField
                      input-id="edit-vendor-active"
                      label="Active"
                      v-model="form.active"
                      flex
                    />
                  </div>
                  <div class="edit_vendor-form-group edit_vendor-form-group--label-dropdown">
                    <LabelDropdown
                      label="Vendor Type"
                      select-id="edit-vendor-role"
                      v-model="form.vendorRole"
                      wide
                      :searchable="true"
                      :options="vendorRoleDropdownOptions"
                    />
                  </div>
                </div>
                <div class="edit_vendor-form-group-row">
                  <div class="edit_vendor-form-group edit_vendor-form-group--label-date">
                    <LabelDateField
                      input-id="edit-vendor-vendor-since"
                      label="Vendor Since"
                      v-model="form.vendorSince"
                      flex
                      @change="handleVendorSinceChange"
                    />
                  </div>
                  <div class="edit_vendor-form-group edit_vendor-form-group--label-input">
                    <LabelInputField
                      input-id="edit-vendor-opening-balance"
                      label="Opening Balance (USD)"
                      v-model="form.openingBalance"
                      type="number"
                      flex
                      disabled
                    />
                  </div>
                </div>
                <div class="edit_vendor-form-group-row">
                  <div class="edit_vendor-form-group edit_vendor-form-group--label-date">
                    <LabelDateField
                      input-id="edit-vendor-balance-as-of"
                      label="Balance as of"
                      v-model="form.balanceAsOf"
                      flex
                      @change="handleBalanceAsOfChange"
                    />
                  </div>
                  <div class="edit_vendor-form-group edit_vendor-form-group--label-input">
                    <LabelInputField
                      input-id="edit-vendor-balance"
                      label="Balance (USD)"
                      v-model="form.balance"
                      type="number"
                      flex
                    />
                  </div>
                </div>
                <div class="edit_vendor-form-group-row">
                  <div class="edit_vendor-form-group edit_vendor-form-group--label-input">
                    <LabelInputField
                      input-id="edit-vendor-tax-id"
                      label="Tax ID"
                      v-model="form.taxId"
                      flex
                    />
                  </div>
                  <div class="edit_vendor-form-group edit_vendor-form-group--label-input">
                    <LabelInputField
                      input-id="edit-vendor-phone"
                      label="Phone"
                      v-model="form.phone"
                      flex
                    />
                  </div>
                </div>
                <div class="edit_vendor-form-group-row">
                  <div class="edit_vendor-form-group edit_vendor-form-group--label-input">
                    <LabelInputField
                      input-id="edit-vendor-fax"
                      label="Fax"
                      v-model="form.fax"
                      flex
                    />
                  </div>
                  <div class="edit_vendor-form-group edit_vendor-form-group--label-input">
                    <LabelInputField
                      input-id="edit-vendor-email"
                      label="Email"
                      v-model="form.email"
                      type="email"
                      flex
                      :has-error="!!vendorEmailError"
                      @blur="validateVendorEmail"
                      @input="clearVendorEmailError"
                    >
                      <div v-if="checkingVendorEmail" class="edit_vendor-checking-text">Checking availability...</div>
                      <div v-if="vendorEmailError" class="edit_vendor-error-text">{{ vendorEmailError }}</div>
                    </LabelInputField>
                  </div>
                </div>
                <div class="edit_vendor-form-group-row">
                  <div class="edit_vendor-form-group edit_vendor-form-group--label-input">
                    <LabelInputField
                      input-id="edit-vendor-webpage"
                      label="Web page address"
                      v-model="form.webpage"
                      flex
                    />
                  </div>
                </div>
              </div>
              <div class="edit_vendor-form-group edit_vendor-full-width edit_vendor-form-group--label-description">
                <LabelDescriptionField
                  input-id="edit-vendor-notes"
                  label="Notes"
                  v-model="form.notes"
                  :rows="3"
                  flex
                />
              </div>
            </div>
          </div>

          <div class="glass-tab-view__panel" v-else-if="activeTab === 'details'">
            <div class="glass-tab-panel glass-tab-panel--flex-column glass-tab-panel--stagger-rows">
              <div class="edit_vendor-form-section">
                <div class="edit_vendor-form-group-row">
                  <div class="edit_vendor-form-group edit_vendor-form-group--label-dropdown">
                    <LabelDropdown
                      label="Payment Terms"
                      select-id="edit-vendor-payment-terms"
                      v-model="paymentTermIdModel"
                      wide
                      required
                      :searchable="true"
                      :options="paymentTermsDropdownOptions"
                      :has-error="showErrors && !form.paymentTermId"
                      @change="handlePaymentTermChange"
                    />
                    <div v-if="showErrors && !form.paymentTermId" class="edit_vendor-error-text">Payment Terms is required.</div>
                  </div>
                  <div class="edit_vendor-form-group edit_vendor-form-group--label-dropdown">
                    <LabelDropdown
                      label="Payment Method"
                      select-id="edit-vendor-payment-method"
                      v-model="form.paymentMethod"
                      wide
                      required
                      :options="paymentMethodDropdownOptions"
                      :has-error="showErrors && !form.paymentMethod"
                    />
                    <div v-if="showErrors && !form.paymentMethod" class="edit_vendor-error-text">Payment Method is required.</div>
                  </div>
                </div>
                <div class="edit_vendor-form-group-row">
                  <div class="edit_vendor-form-group edit_vendor-form-group--label-input">
                    <LabelInputField
                      input-id="edit-vendor-bank-name"
                      label="Bank Name"
                      v-model="form.bankName"
                      placeholder="Bank Name"
                      flex
                    />
                  </div>
                  <div class="edit_vendor-form-group edit_vendor-form-group--label-dropdown">
                    <LabelDropdown
                      label="Bank Account Type"
                      select-id="edit-vendor-bank-account-type"
                      v-model="form.bankAccountType"
                      wide
                      :options="bankAccountTypeDropdownOptions"
                    />
                  </div>
                </div>
                <div class="edit_vendor-form-group-row">
                  <div class="edit_vendor-form-group edit_vendor-form-group--label-input">
                    <LabelInputField
                      input-id="edit-vendor-bank-routing"
                      label="Bank Routing Number"
                      v-model="form.bankRoutingNumber"
                      placeholder="Bank Routing Number"
                      flex
                    />
                  </div>
                  <div class="edit_vendor-form-group edit_vendor-form-group--label-input">
                    <LabelInputField
                      input-id="edit-vendor-account-number"
                      label="Account Number"
                      v-model="form.accountNumber"
                      placeholder="Account Number"
                      flex
                    />
                  </div>
                </div>
                <div class="edit_vendor-form-group-row">
                  <div class="edit_vendor-form-group edit_vendor-form-group--label-input">
                    <LabelInputField
                      input-id="edit-vendor-bank-branch"
                      label="Bank Branch"
                      v-model="form.bankBranch"
                      placeholder="Bank Branch"
                      flex
                    />
                  </div>
                  <div class="edit_vendor-form-group edit_vendor-form-group--label-input">
                    <LabelInputField
                      input-id="edit-vendor-credit-limit"
                      label="Credit Limit(USD)"
                      v-model="form.creditLimit"
                      type="number"
                      flex
                    />
                  </div>
                </div>
                <div class="edit_vendor-form-group-row">
                  <div class="edit_vendor-form-group edit_vendor-form-group--label-dropdown">
                    <LabelDropdown
                      label="Account"
                      select-id="edit-vendor-account"
                      v-model="accountIdModel"
                      wide
                      :searchable="true"
                      :options="accountDropdownOptions"
                      @change="handleAccountChange"
                    />
                  </div>
                  <div class="edit_vendor-form-group edit_vendor-form-group--label-dropdown">
                    <LabelDropdown
                      label="Preferred shipping method"
                      select-id="edit-vendor-shipping-method"
                      v-model="shippingMethodIdModel"
                      wide
                      :searchable="true"
                      :options="shippingMethodDropdownOptions"
                      @change="handleShippingMethodChange"
                    />
                  </div>
                </div>
                <div class="edit_vendor-form-group-row">
                  <div class="edit_vendor-form-group edit_vendor-form-group--label-dropdown">
                    <LabelDropdown
                      label="Vendor Group"
                      select-id="edit-vendor-vendor-group"
                      v-model="vendorGroupIdModel"
                      wide
                      :searchable="true"
                      :options="vendorGroupDropdownOptions"
                      @change="handleVendorGroupChange"
                    />
                  </div>
                  <div class="edit_vendor-form-group edit_vendor-form-group--label-input">
                    <LabelInputField
                      input-id="edit-vendor-tax-registration"
                      label="TAX Registration Number"
                      v-model="form.taxRegistrationNumber"
                      placeholder="TAX Registration Number"
                      flex
                    />
                  </div>
                </div>
                <div class="edit_vendor-form-group-row">
                  <div class="edit_vendor-form-group edit_vendor-form-group--label-dropdown">
                    <LabelDropdown
                      label="Tax Code"
                      select-id="edit-vendor-tax-code"
                      v-model="form.taxCode"
                      wide
                      :options="taxCodeDropdownOptions"
                    />
                  </div>
                  <div class="edit_vendor-form-group edit_vendor-form-group--label-input">
                    <LabelInputField
                      input-id="edit-vendor-service-tax"
                      label="Service tax registration no"
                      v-model="form.serviceTaxRegistrationNo"
                      placeholder="Service tax registration no"
                      flex
                    />
                  </div>
                </div>
                <div class="edit_vendor-form-group-row">
                  <div class="edit_vendor-form-group edit_vendor-full-width edit_vendor-custom-fields-row">
                    <span class="edit_vendor-custom-fields-label">Custom Fields</span>
                    <SecondaryButton
                      label="Manage Custom Fields"
                      icon="bi bi-sliders"
                      @click="openCustomFieldsModal"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="glass-tab-view__panel" v-else-if="activeTab === 'contacts'">
            <div class="glass-tab-panel glass-tab-panel--flex-column glass-tab-panel--stagger-rows">
              <div class="edit_vendor-contacts-section">
                <div class="edit_vendor-contacts-header">
                  <h3>Contacts</h3>
                  <div class="edit_vendor-add-contact-container">
                    <PrimaryButton
                      label="Add Contact"
                      icon="bi bi-plus-lg"
                      @click="addContactRow"
                    />
                    <div v-if="contacts.length === 0 && newContacts.length === 0" class="edit_vendor-add-contact-tooltip">
                      <div class="edit_vendor-tooltip-content">
                        <div class="edit_vendor-tooltip-icon">👥</div>
                        <p>No contacts added yet</p>
                        <p class="edit_vendor-tooltip-subtitle">Add contacts for this vendor</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="edit_vendor-contacts-grid">
                  <div v-for="(contact, idx) in contacts" :key="'contact-' + idx" class="edit_vendor-contact-card">
                    <div class="edit_vendor-contact-card-header">
                      <LabelCheckboxField
                        :input-id="`edit-vendor-contact-primary-${idx}`"
                        :label="contacts.length === 1 ? 'Primary Contact *' : 'Primary Contact'"
                        v-model="contact.primary"
                        custom-class="label-checkbox-field--card-compact"
                        :disabled="contacts.length === 1 || (contact.primary && getNumberOfPrimaryContacts() === 1)"
                        @change="() => setPrimaryContact(idx)"
                      />
                    </div>
                    <div class="edit_vendor-contact-fields">
                      <div class="edit_vendor-contact-field-row">
                        <div class="edit_vendor-contact-field edit_vendor-form-group--label-input">
                          <LabelInputField
                            :input-id="`edit-vendor-contact-name-${idx}`"
                            label="Contact Name"
                            v-model="contact.name"
                            flex
                            :readonly="editingContactIdx !== idx"
                          />
                        </div>
                        <div class="edit_vendor-contact-field edit_vendor-form-group--label-input">
                          <LabelInputField
                            :input-id="`edit-vendor-contact-title-${idx}`"
                            label="Title"
                            v-model="contact.title"
                            flex
                            :readonly="editingContactIdx !== idx"
                          />
                        </div>
                      </div>
                      <div class="edit_vendor-contact-field-row">
                        <div class="edit_vendor-contact-field edit_vendor-form-group--label-input">
                          <LabelInputField
                            :input-id="`edit-vendor-contact-phone-${idx}`"
                            label="Business Phone"
                            v-model="contact.phone"
                            flex
                            :readonly="editingContactIdx !== idx"
                          />
                        </div>
                        <div class="edit_vendor-contact-field edit_vendor-form-group--label-input">
                          <LabelInputField
                            :input-id="`edit-vendor-contact-email-${idx}`"
                            label="Email"
                            v-model="contact.email"
                            type="email"
                            flex
                            :readonly="editingContactIdx !== idx"
                            :has-error="!!contactEmailErrors[`contact-${idx}`]"
                            @blur="validateContactEmail(contact.email, idx, false)"
                            @input="handleContactEmailInput(contact.email, idx, false)"
                          >
                            <div v-if="checkingContactEmails[`contact-${idx}`]" class="edit_vendor-checking-text">Checking availability...</div>
                            <div v-if="contactEmailErrors[`contact-${idx}`]" class="edit_vendor-error-text">{{ contactEmailErrors[`contact-${idx}`] }}</div>
                          </LabelInputField>
                        </div>
                      </div>
                    </div>
                    <div v-if="editingContactIdx !== idx" class="edit_vendor-contact-save-actions">
                      <SecondaryButton label="Edit" icon="bi bi-pencil" @click.prevent="editContact(idx)" />
                      <SecondaryButton
                        label="Delete"
                        icon="bi bi-trash"
                        custom-class="consultant-secondary-btn--danger"
                        @click.prevent="deleteContact(idx)"
                      />
                    </div>
                    <div v-if="editingContactIdx === idx" class="edit_vendor-contact-save-actions">
                      <SecondaryButton label="Cancel" icon="bi bi-x-lg" @click.prevent="cancelEditContact" />
                      <PrimaryButton label="Update" icon="bi bi-check-lg" @click.prevent="updateContact()" />
                    </div>
                  </div>
                  <div v-for="(nc, nidx) in newContacts" :key="'new-contact-' + nidx" class="edit_vendor-contact-card edit_vendor-contact-card-new">
                    <div class="edit_vendor-contact-card-header">
                      <LabelCheckboxField
                        :input-id="`edit-vendor-new-contact-primary-${nidx}`"
                        :label="contacts.length + newContacts.length === 1 ? 'Primary Contact *' : 'Primary Contact'"
                        v-model="nc.primary"
                        custom-class="label-checkbox-field--card-compact"
                        :disabled="(contacts.length + newContacts.length === 1) || (nc.primary && getNumberOfPrimaryContacts() === 1)"
                        @change="setPrimaryContactNew(nidx)"
                      />
                    </div>
                    <div class="edit_vendor-contact-fields">
                      <div class="edit_vendor-contact-field-row">
                        <div class="edit_vendor-contact-field edit_vendor-form-group--label-input">
                          <LabelInputField
                            :input-id="`edit-vendor-new-contact-name-${nidx}`"
                            label="Contact Name"
                            v-model="nc.name"
                            placeholder="Contact Name"
                            flex
                          />
                        </div>
                        <div class="edit_vendor-contact-field edit_vendor-form-group--label-input">
                          <LabelInputField
                            :input-id="`edit-vendor-new-contact-title-${nidx}`"
                            label="Title"
                            v-model="nc.title"
                            placeholder="Title"
                            flex
                          />
                        </div>
                      </div>
                      <div class="edit_vendor-contact-field-row">
                        <div class="edit_vendor-contact-field edit_vendor-form-group--label-input">
                          <LabelInputField
                            :input-id="`edit-vendor-new-contact-phone-${nidx}`"
                            label="Business Phone"
                            v-model="nc.phone"
                            placeholder="Business Phone"
                            flex
                          />
                        </div>
                        <div class="edit_vendor-contact-field edit_vendor-form-group--label-input">
                          <LabelInputField
                            :input-id="`edit-vendor-new-contact-email-${nidx}`"
                            label="Email"
                            v-model="nc.email"
                            type="email"
                            placeholder="Email"
                            flex
                            :has-error="!!contactEmailErrors[`new-contact-${nidx}`]"
                            @blur="validateContactEmail(nc.email, nidx, true)"
                            @input="handleContactEmailInput(nc.email, nidx, true)"
                          >
                            <div v-if="checkingContactEmails[`new-contact-${nidx}`]" class="edit_vendor-checking-text">Checking availability...</div>
                            <div v-if="contactEmailErrors[`new-contact-${nidx}`]" class="edit_vendor-error-text">{{ contactEmailErrors[`new-contact-${nidx}`] }}</div>
                          </LabelInputField>
                        </div>
                      </div>
                    </div>
                    <div class="edit_vendor-contact-save-actions">
                      <SecondaryButton label="Cancel" icon="bi bi-x-lg" @click.prevent="cancelContact(nidx)" />
                      <PrimaryButton label="Save" icon="bi bi-check-lg" @click.prevent="saveContact(nidx)" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="glass-tab-view__panel" v-else-if="activeTab === 'addresses'">
            <div class="glass-tab-panel glass-tab-panel--flex-column glass-tab-panel--stagger-rows">
              <div class="edit_vendor-contacts-section">
                <div class="edit_vendor-addresses-header">
                  <h3>Addresses</h3>
                  <div class="edit_vendor-add-address-container">
                    <PrimaryButton
                      label="Add Address"
                      icon="bi bi-plus-lg"
                      @click="addNewAddressRow"
                    />
                    <div v-if="addresses.length === 0 && newAddresses.length === 0" class="edit_vendor-add-address-tooltip">
                      <div class="edit_vendor-tooltip-content">
                        <div class="edit_vendor-tooltip-icon">📍</div>
                        <p>No addresses added yet</p>
                        <p class="edit_vendor-tooltip-subtitle">Add addresses for this vendor</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="edit_vendor-addresses-grid">
                  <div v-for="(address, idx) in addresses" :key="'address-' + idx" class="edit_vendor-address-card">
                    <div class="edit_vendor-address-card-header edit_vendor-form-group--label-dropdown">
                      <LabelDropdown
                        label="Address Type"
                        :select-id="`edit-vendor-address-type-${idx}`"
                        v-model="address.type"
                        wide
                        required
                        :disabled="editingAddressIdx !== idx"
                        :options="addressTypeDropdownOptions(idx, false)"
                      />
                    </div>
                    <div class="edit_vendor-address-fields">
                      <div class="edit_vendor-address-field-row">
                        <div class="edit_vendor-address-field edit_vendor-form-group--label-input">
                          <LabelInputField
                            :input-id="`edit-vendor-address-line1-${idx}`"
                            label="Address Line 1"
                            v-model="address.address1"
                            placeholder="Street address"
                            flex
                            :readonly="editingAddressIdx !== idx"
                          />
                        </div>
                        <div class="edit_vendor-address-field edit_vendor-form-group--label-input">
                          <LabelInputField
                            :input-id="`edit-vendor-address-line2-${idx}`"
                            label="Address Line 2"
                            v-model="address.address2"
                            placeholder="Apartment, suite, etc."
                            flex
                            :readonly="editingAddressIdx !== idx"
                          />
                        </div>
                      </div>
                      <AddressSelector
                        :country="address.country"
                        :state="address.state"
                        :city="address.city"
                        :postal-code="address.postalCode"
                        :disabled="editingAddressIdx !== idx"
                        @update:country="address.country = $event"
                        @update:state="address.state = $event"
                        @update:city="address.city = $event"
                        @update:postalCode="address.postalCode = $event"
                      />
                    </div>
                    <div v-if="editingAddressIdx !== idx" class="edit_vendor-address-save-actions">
                      <SecondaryButton label="Edit" icon="bi bi-pencil" @click.prevent="editAddress(idx)" />
                      <SecondaryButton
                        label="Delete"
                        icon="bi bi-trash"
                        custom-class="consultant-secondary-btn--danger"
                        @click.prevent="deleteAddress(idx)"
                      />
                    </div>
                    <div v-if="editingAddressIdx === idx" class="edit_vendor-address-save-actions">
                      <SecondaryButton label="Cancel" icon="bi bi-x-lg" @click.prevent="cancelEditAddress" />
                      <PrimaryButton label="Update" icon="bi bi-check-lg" @click.prevent="updateAddress()" />
                    </div>
                  </div>
                  <div v-for="(na, nidx) in newAddresses" :key="'new-address-' + nidx" class="edit_vendor-address-card edit_vendor-address-card-new">
                    <div class="edit_vendor-address-card-header edit_vendor-form-group--label-dropdown">
                      <LabelDropdown
                        label="Address Type"
                        :select-id="`edit-vendor-new-address-type-${nidx}`"
                        v-model="na.type"
                        wide
                        required
                        :options="addressTypeDropdownOptions(nidx, true)"
                      />
                    </div>
                    <div class="edit_vendor-address-fields">
                      <div class="edit_vendor-address-field-row">
                        <div class="edit_vendor-address-field edit_vendor-form-group--label-input">
                          <LabelInputField
                            :input-id="`edit-vendor-new-address-line1-${nidx}`"
                            label="Address Line 1"
                            v-model="na.address1"
                            placeholder="Street address"
                            flex
                          />
                        </div>
                        <div class="edit_vendor-address-field edit_vendor-form-group--label-input">
                          <LabelInputField
                            :input-id="`edit-vendor-new-address-line2-${nidx}`"
                            label="Address Line 2"
                            v-model="na.address2"
                            placeholder="Apartment, suite, etc."
                            flex
                          />
                        </div>
                      </div>
                      <AddressSelector
                        :country="na.country"
                        :state="na.state"
                        :city="na.city"
                        :postal-code="na.postalCode"
                        @update:country="na.country = $event"
                        @update:state="na.state = $event"
                        @update:city="na.city = $event"
                        @update:postalCode="na.postalCode = $event"
                      />
                    </div>
                    <div class="edit_vendor-address-save-actions">
                      <SecondaryButton label="Cancel" icon="bi bi-x-lg" @click.prevent="cancelNewAddress(nidx)" />
                      <PrimaryButton label="Save" icon="bi bi-check-lg" @click.prevent="saveNewAddress(nidx)" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="glass-tab-view__panel" v-else-if="activeTab === 'documents'">
            <div class="glass-tab-panel glass-tab-panel--flex-column glass-tab-panel--stagger-rows">
              <div class="edit_vendor-documents-section">
                <div class="edit_vendor-documents-header">
                  <h3>Documents</h3>
                  <div class="edit_vendor-add-address-container">
                    <PrimaryButton
                      label="Add Document"
                      icon="bi bi-plus-lg"
                      @click="$refs.addDocumentFileInput.click()"
                    />
                    <input
                      id="add-vendor-document-input"
                      type="file"
                      ref="addDocumentFileInput"
                      class="edit_vendor-document-file-input"
                      @change="handleDocumentFileUpload"
                      multiple
                    />
                  </div>
                </div>
                <div class="edit_vendor-documents-split-view">
                  <div class="edit_vendor-documents-list-container">
                    <div v-if="documents.length > 0" class="edit_vendor-documents-list">
                      <div
                        v-for="(document, index) in documents"
                        :key="document.id || index"
                        class="edit_vendor-document-item"
                        :class="{ 'active': selectedDocumentIndex === index }"
                        @click="selectDocument(index)"
                      >
                        <div class="edit_vendor-document-info">
                          <div class="edit_vendor-document-name">
                            <i class="bi bi-file-earmark"></i> {{ document.originalFileName || document.fileName }}
                          </div>
                          <div class="edit_vendor-document-meta">
                            <span class="edit_vendor-document-size">{{ formatFileSize(document.fileSize) }}</span>
                            <span class="edit_vendor-document-date">{{ formatDate(document.uploadedAt) }}</span>
                          </div>
                        </div>
                        <SecondaryButton
                          label=""
                          icon="bi bi-trash"
                          custom-class="consultant-secondary-btn--danger edit_vendor-document-delete-btn"
                          title="Remove document"
                          @click.stop="removeVendorDocument(index)"
                        />
                      </div>
                    </div>
                    <div v-else class="edit_vendor-no-documents-message">
                      <div class="edit_vendor-no-documents-icon">📄</div>
                      <p>No documents added yet</p>
                      <p class="edit_vendor-tooltip-subtitle">Click "Add Document" to upload files</p>
                    </div>
                  </div>
                  <div class="edit_vendor-document-preview-container">
                    <div v-if="selectedDocumentIndex !== null && documents[selectedDocumentIndex]" class="edit_vendor-document-preview">
                      <div class="edit_vendor-document-preview-header">
                        <h4>{{ documents[selectedDocumentIndex].originalFileName || documents[selectedDocumentIndex].fileName }}</h4>
                      </div>
                      <div class="edit_vendor-document-preview-content">
                        <div v-if="loadingPreview" class="edit_vendor-document-preview-loading">
                          <div class="edit_vendor-document-preview-icon">
                            <i class="bi bi-arrow-repeat" style="font-size: 2rem; color: #0099cc; animation: spin 1s linear infinite;"></i>
                          </div>
                          <p>Loading preview...</p>
                        </div>
                        <div v-else-if="currentPreviewUrl && isImageFile(documents[selectedDocumentIndex])" class="edit_vendor-document-preview-image">
                          <img :src="currentPreviewUrl" :alt="documents[selectedDocumentIndex].originalFileName || documents[selectedDocumentIndex].fileName" @error="handlePreviewError" />
                        </div>
                        <div v-else-if="currentPreviewUrl && isPdfFile(documents[selectedDocumentIndex])" class="edit_vendor-document-preview-pdf">
                          <iframe :src="currentPreviewUrl" frameborder="0" @error="handlePreviewError"></iframe>
                        </div>
                        <div v-else-if="previewError" class="edit_vendor-document-preview-error">
                          <div class="edit_vendor-document-preview-icon">
                            <i class="bi bi-exclamation-triangle" style="font-size: 4rem; color: #dc3545;"></i>
                          </div>
                          <p>Failed to load preview</p>
                          <PrimaryButton
                            label="Open in New Window"
                            icon="bi bi-box-arrow-up-right"
                            @click="openDocumentInNewWindow(documents[selectedDocumentIndex])"
                          />
                        </div>
                        <div v-else class="edit_vendor-document-preview-unsupported">
                          <div class="edit_vendor-document-preview-icon">
                            <i class="bi bi-file-earmark" style="font-size: 4rem; color: #ccc;"></i>
                          </div>
                          <p>Preview not available for this file type</p>
                          <PrimaryButton
                            label="Open in New Window"
                            icon="bi bi-box-arrow-up-right"
                            @click="openDocumentInNewWindow(documents[selectedDocumentIndex])"
                          />
                        </div>
                      </div>
                    </div>
                    <div v-else class="edit_vendor-document-preview-empty">
                      <div class="edit_vendor-document-preview-icon">
                        <i class="bi bi-file-earmark-text" style="font-size: 4rem; color: #ccc;"></i>
                      </div>
                      <p>Select a document to preview</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="edit_vendor-form-actions glass-tab-view__actions">
        <SecondaryButton label="Cancel" icon="bi bi-x-lg" @click="handleCancel" />
        <PrimaryButton label="Save Changes" icon="bi bi-check-lg" @click="handleSave" />
      </div>
    </form>

    <ManageCustomFields v-if="showCustomFieldsModal" @close="closeCustomFieldsModal" />
    <AddNewVendorGroup
      :visible="showAddGroupModal"
      :companyId="$route.params.id"
      @ok="onAddGroupOk"
      @cancel="showAddGroupModal = false"
    />
    <AddPreferredShippingMethod
      :visible="showAddShippingMethodModal"
      :companyId="$route.params.id"
      @ok="onAddShippingMethodOk"
      @cancel="showAddShippingMethodModal = false"
    />
    <AddNewPaymentTerm
      :visible="showAddPaymentTermModal"
      :companyId="$route.params.id"
      @ok="onAddPaymentTermOk"
      @cancel="showAddPaymentTermModal = false"
    />

    <ErrorModal
      :show="showErrorModal"
      :title="errorTitle"
      :message="errorMessage"
      @close="closeErrorModal"
    />

    <SuccessModal
      :show="showSuccessModal"
      :title="successTitle"
      :message="successMessage"
      :button-text="successAskStay ? 'Yes' : 'OK'"
      :secondary-button-text="successAskStay ? 'No' : ''"
      @close="stayOnPageAfterSave"
      @secondary="leavePageAfterSave"
    />

    <WarningModal
      :show="showWarningModal"
      :title="warningTitle"
      :message="warningMessage"
      :buttonText="'Cancel'"
      :confirmButtonText="warningConfirmButtonText"
      @close="closeWarningModal"
      @confirm="confirmDelete"
    />
  </div>
</template>

<script>
import '../styles/EditVendor.css';
import '../styles/edit-vendor-glass.css';
import ManageCustomFields from '@/components/ManageCustomFields.vue';
import logger from '@/utils/logger';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import TabNavigation from '@/components/TabNavigation.vue';
import { VENDOR_ROLES_FOR_SELECT } from '@/constants/vendorRoles.js';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';
import LabelInputFieldComponent from '@/modules/consultant/components/LabelInputField.vue';
import LabelCheckboxFieldComponent from '@/modules/consultant/components/LabelCheckboxField.vue';
import LabelDescriptionFieldComponent from '@/modules/consultant/components/LabelDescriptionField.vue';
import LabelDropdownComponent from '@/modules/consultant/components/LabelDropdown.vue';
import LabelDateFieldComponent from '@/modules/consultant/components/LabelDateField.vue';
import ApiService from '@/services/api.service.js';
import CompanyService from '@/services/company.service.js';
import vendorService from '@/services/vendor.service.js';
import AddNewVendorGroup from '@/modules/company/views/AddNewVendorGroup.vue';
import AddNewPaymentTerm from '@/modules/company/views/AddNewPaymentTerm.vue';
import AddPreferredShippingMethod from '@/modules/vendor/views/AddPreferredShippingMethod.vue';
import ErrorModal from '@/components/ErrorModal.vue';
import SuccessModal from '@/components/SuccessModal.vue';
import WarningModal from '@/components/WarningModal.vue';
import AddressSelector from '@/components/AddressSelector.vue';

export default {
  name: 'EditVendor',
  components: { 
    ManageCustomFields, 
    BrucdcumHeader,
    TabNavigation,
    AddNewVendorGroup, 
    AddNewPaymentTerm, 
    AddPreferredShippingMethod,
    ErrorModal,
    SuccessModal,
    WarningModal,
    AddressSelector,
    PrimaryButton,
    SecondaryButton,
    LabelInputField: LabelInputFieldComponent,
    LabelCheckboxField: LabelCheckboxFieldComponent,
    LabelDescriptionField: LabelDescriptionFieldComponent,
    LabelDropdown: LabelDropdownComponent,
    LabelDateField: LabelDateFieldComponent,
  },
  data() {
    return {
      loading: true,
      activeTab: 'general',
      tabs: [
        { id: 'general', name: 'Basic Information' },
        { id: 'details', name: 'Payment & Settings' },
        { id: 'contacts', name: 'Contact Details' },
        { id: 'addresses', name: 'Address Information' },
        { id: 'documents', name: 'Documents' }
      ],
      showErrors: false,
      showErrorModal: false,
      errorTitle: 'Validation Error',
      errorMessage: '',
      validationErrors: [],
      errorTabs: [],
      showSuccessModal: false,
      successTitle: 'Success',
      successMessage: '',
      navigateOnSuccess: false,
      successAskStay: false,
      vendorId: null,
      companyId: null,
      form: {
        vendorName: '',
        vendorRole: '',
        active: true,
        vendorSince: '',
        taxId: '',
        openingBalance: 0,
        balanceAsOf: '',
        balance: 0,
        phone: '',
        fax: '',
        email: '',
        webpage: '',
        notes: '',
        accountId: '',
        creditLimit: 0,
        shippingMethodId: '',
        paymentMethod: '',
        paymentTermId: '',
        accountNumber: '',
        bankName: '',
        bankBranch: '',
        bankAccountType: '',
        bankRoutingNumber: '',
        vendorGroupId: '',
        taxRegistrationNumber: '',
        taxCode: '',
        serviceTaxRegistrationNo: '',
        addressDetails: ''
      },
      contacts: [],
      newContacts: [],
      addresses: [],
      newAddresses: [],
      editingContactIdx: null,
      editingAddressIdx: null,
      selectedAddressType: '',
      accounts: [],
      filteredAccounts: [],
      paymentTermsList: [],
      vendorGroups: [],
      shippingMethodsList: [],
      showCustomFieldsModal: false,
      showAddGroupModal: false,
      showAddPaymentTermModal: false,
      showAddShippingMethodModal: false,
      previousVendorGroup: '',
      previousPaymentTerm: '',
      previousShippingMethod: '',
      vendorIdExists: false,
      checkingVendorId: false,
      vendorIdCheckTimeout: null,
      originalVendorId: '',
      originalVendorEmail: '',
      vendorEmailError: '',
      checkingVendorEmail: false,
      vendorEmailCheckTimeout: null,
      contactEmailErrors: {},
      checkingContactEmails: {},
      contactEmailCheckTimeouts: {},
      originalContactEmails: {},
      documents: [],
      selectedDocumentIndex: null,
      documentPreviewUrls: {}, // Cache for preview URLs
      currentPreviewUrl: '', // Current preview URL for selected document
      loadingPreview: false,
      previewError: false,
      documentsToDelete: [], // Track documents to delete
      documentsToUpload: [], // Track new documents to upload
      documentToDelete: null,
      showWarningModal: false,
      warningTitle: 'Warning',
      warningMessage: '',
      warningConfirmButtonText: 'Delete'
    };
  },
  computed: {
    availableAddressTypes() {
      const types = this.addresses.map(addr => addr.type).filter(Boolean);
      return [...new Set(types)];
    },
    hasBillingAddress() {
      return this.addresses.some(addr => addr.type === 'Billing');
    },
    hasShippingAddress() {
      return this.addresses.some(addr => addr.type === 'Shipping');
    },
    hasCurrentTabErrors() {
      return this.validateCurrentTab();
    },
    paymentTermIdModel: {
      get() {
        return this.form.paymentTermId === '' || this.form.paymentTermId == null ? '' : String(this.form.paymentTermId);
      },
      set(v) {
        this.form.paymentTermId = v;
      },
    },
    accountIdModel: {
      get() {
        return this.form.accountId === '' || this.form.accountId == null ? '' : String(this.form.accountId);
      },
      set(v) {
        this.form.accountId = v;
      },
    },
    shippingMethodIdModel: {
      get() {
        return this.form.shippingMethodId === '' || this.form.shippingMethodId == null ? '' : String(this.form.shippingMethodId);
      },
      set(v) {
        this.form.shippingMethodId = v;
      },
    },
    vendorGroupIdModel: {
      get() {
        return this.form.vendorGroupId === '' || this.form.vendorGroupId == null ? '' : String(this.form.vendorGroupId);
      },
      set(v) {
        this.form.vendorGroupId = v;
      },
    },
    paymentTermsDropdownOptions() {
      return [
        { value: '', label: 'Select' },
        ...(this.paymentTermsList || []).map((term) => ({
          value: String(term.id),
          label: `${term.name} (${term.description})`,
        })),
        { value: '__add_new__', label: 'Add New Payment Term' },
      ];
    },
    paymentMethodDropdownOptions() {
      const methods = [
        'Cash',
        'Cheque',
        'Credit Card',
        'Direct Deposit',
        'Master card',
        'Online Banking',
        'Standing Order',
        'Switch/Maestro',
      ];
      return [
        { value: '', label: 'Select' },
        ...methods.map((method) => ({ value: method, label: method })),
      ];
    },
    bankAccountTypeDropdownOptions() {
      return [
        { value: '', label: 'Select' },
        { value: 'Checking', label: 'Checking' },
        { value: 'Savings', label: 'Savings' },
      ];
    },
    accountDropdownOptions() {
      return [
        { value: '', label: 'Select account' },
        ...(this.filteredAccounts || []).map((acc) => ({
          value: String(acc.id),
          label: `${acc.accountNumber} - ${acc.accountName} - ${acc.accountType}`,
        })),
        { value: '__add_new__', label: 'Add New Account' },
      ];
    },
    shippingMethodDropdownOptions() {
      return [
        { value: '', label: 'Select' },
        ...(this.shippingMethodsList || []).map((method) => ({
          value: String(method.id),
          label: method.name,
        })),
        { value: '__add_new__', label: 'Add New Shipping Method' },
      ];
    },
    vendorGroupDropdownOptions() {
      return [
        { value: '', label: 'Select' },
        ...(this.vendorGroups || []).map((group) => ({
          value: String(group.id),
          label: group.name,
        })),
        { value: '__create_new__', label: 'Add New Group' },
      ];
    },
    vendorRoleDropdownOptions() {
      return [
        { value: '', label: 'Select' },
        ...VENDOR_ROLES_FOR_SELECT,
      ];
    },
    taxCodeDropdownOptions() {
      return [{ value: '', label: 'Select' }];
    },
  },
  methods: {
    resolveSelectValue(eventOrValue) {
      if (eventOrValue && typeof eventOrValue === 'object' && eventOrValue.target) {
        return eventOrValue.target.value;
      }
      return eventOrValue;
    },
    addressTypeDropdownOptions(idx, isNew) {
      const types = this.getAvailableAddressTypes(idx, isNew);
      return [
        { value: '', label: 'Select Type' },
        ...types.map((type) => ({ value: type, label: type })),
      ];
    },
    handleTabChange(newTab) {
      this.activeTab = newTab;
    },
    clearVendorIdError() {
      // Clear vendor ID error when user starts typing
      if (this.form.vendorId && this.form.vendorId.trim() !== '') {
        this.showErrors = false;
      }
    },
    clearVendorEmailError() {
      this.vendorEmailError = '';
      this.checkingVendorEmail = false;
      if (this.vendorEmailCheckTimeout) {
        clearTimeout(this.vendorEmailCheckTimeout);
        this.vendorEmailCheckTimeout = null;
      }
    },
    async validateVendorEmail() {
      if (!this.form.email || !this.form.email.trim()) {
        return;
      }
      
      // Basic email format validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.form.email.trim())) {
        this.vendorEmailError = 'Please enter a valid email address.';
        return;
      }
      
      // Check if email is being changed
      const originalEmail = (this.originalVendorEmail || '').toLowerCase().trim();
      const newEmail = this.form.email.toLowerCase().trim();
      
      if (newEmail === originalEmail) {
        // Email hasn't changed, no need to check
        return;
      }
      
      // Clear previous timeout
      if (this.vendorEmailCheckTimeout) {
        clearTimeout(this.vendorEmailCheckTimeout);
      }
      
      // Debounce the API call
      this.checkingVendorEmail = true;
      this.vendorEmailError = '';
      
      this.vendorEmailCheckTimeout = setTimeout(async () => {
        try {
          const emailCheck = await vendorService.checkVendorEmailExists(this.form.email.trim(), this.companyId, this.vendorId);
          
          if (emailCheck.data.exists) {
            if (emailCheck.data.existsAsConsultant) {
              this.vendorEmailError = 'This email address already exists for a consultant in this company.';
            } else if (emailCheck.data.existsAsContact) {
              this.vendorEmailError = 'This email address already exists for a customer contact in this company.';
            } else if (emailCheck.data.existsAsVendor) {
              this.vendorEmailError = 'This email address already exists for another vendor in this company.';
            } else {
              this.vendorEmailError = 'This email address is already in use.';
            }
          }
        } catch (error) {
          logger.error('Error checking vendor email:', error);
          // Don't show error to user for validation check failures
        } finally {
          this.checkingVendorEmail = false;
        }
      }, 500); // 500ms debounce
    },
    
    async handleVendorIdInput() {
      this.vendorIdExists = false;
      
      // Only check if vendor ID is not empty and company ID exists
      if (!this.form.vendorId || !this.form.vendorId.trim()) {
        return;
      }
      
      // Don't check if it's the same as the original vendor ID
      if (this.form.vendorId.trim() === this.originalVendorId) {
        this.vendorIdExists = false; // Clear any existing error
        return;
      }
      
      if (!this.companyId) {
        return;
      }
      
      // Debounce the API call
      clearTimeout(this.vendorIdCheckTimeout);
      this.checkingVendorId = true;
      
      this.vendorIdCheckTimeout = setTimeout(async () => {
        try {
          const response = await vendorService.checkVendorIdExists(this.form.vendorId.trim(), this.companyId, this.vendorId);
          this.vendorIdExists = response.data.exists;
        } catch (error) {
          logger.error('Error checking vendor ID:', error);
          // Don't show error to user for validation check failures
        } finally {
          this.checkingVendorId = false;
        }
      }, 500); // 500ms debounce
    },
    clearVendorNameError() {
      // Clear vendor name error when user starts typing
      if (this.form.vendorName && this.form.vendorName.trim() !== '') {
        this.showErrors = false;
      }
    },
    getCurrentDate() {
      const today = new Date();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const year = today.getFullYear();
      return `${month}/${day}/${year}`;
    },
    validateDate(field) {
      const value = this.form[field];
      if (!value) return true;
      
      const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;
      if (!dateRegex.test(value)) return false;
      
      const parts = value.split('/');
      const month = parseInt(parts[0], 10);
      const day = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);
      
      const date = new Date(year, month - 1, day);
      return date.getFullYear() === year && 
             date.getMonth() === month - 1 && 
             date.getDate() === day;
    },
    validateCurrentTab() {
      let hasErrors = false;
      
      if (this.activeTab === 'general') {
        // Validate vendor name and date formats if provided
        if (!this.form.vendorName || this.form.vendorName.trim() === '') {
          this.addValidationError('Vendor Name is required.', 'general');
          hasErrors = true;
        }
        if (this.form.openingBalance < 0) {
          this.addValidationError('Opening Balance cannot be negative.', 'general');
          hasErrors = true;
        }
        if (this.form.vendorSince && !this.validateDate('vendorSince')) {
          this.addValidationError('Please enter a valid date in MM/DD/YYYY format for Vendor Since', 'general');
          hasErrors = true;
        }
        if (this.form.balanceAsOf && !this.validateDate('balanceAsOf')) {
          this.addValidationError('Please enter a valid date in MM/DD/YYYY format for Balance as of', 'general');
          hasErrors = true;
        }
      }
      
      else if (this.activeTab === 'details') {
        // Validate required fields
        if (!this.form.paymentTermId) {
          this.addValidationError('Payment Terms is required.', 'details');
          hasErrors = true;
        }
        if (!this.form.paymentMethod) {
          this.addValidationError('Payment Method is required.', 'details');
          hasErrors = true;
        }
        // Only validate format of optional fields if they are provided
        if (this.form.creditLimit < 0) {
          this.addValidationError('Credit Limit cannot be negative.', 'details');
          hasErrors = true;
        }
        if (this.form.accountNumber && !/^\d+$/.test(this.form.accountNumber)) {
          this.addValidationError('Account Number must contain only digits.', 'details');
          hasErrors = true;
        }
      }
      
      else if (this.activeTab === 'contacts') {
        // Only validate format of contacts if they exist
        const allContacts = [...this.contacts, ...this.newContacts];
        
        if (allContacts.length > 0) {
          // If contacts exist, ensure one is primary
          const hasPrimaryContact = allContacts.some(contact => contact.primary);
          if (!hasPrimaryContact) {
            this.addValidationError('At least one contact must be marked as primary.', 'contacts');
            hasErrors = true;
          }
          
          // Validate format of provided contact information
          allContacts.forEach((contact, index) => {
            if (contact.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) {
              this.addValidationError(`Contact ${index + 1}: Invalid email format.`, 'contacts');
              hasErrors = true;
            }
            if (contact.phone && !/^[\d\s-+()]+$/.test(contact.phone)) {
              this.addValidationError(`Contact ${index + 1}: Invalid phone number format.`, 'contacts');
              hasErrors = true;
            }
          });
        }
      }
      
      else if (this.activeTab === 'addresses') {
        // Only validate format of addresses if they exist
        const allAddresses = [...this.addresses, ...this.newAddresses];
        
        if (allAddresses.length > 0) {
          allAddresses.forEach(address => {
            if (address.type === 'Billing') {
              // Only validate required fields for billing address
              if (!address.address1 || !address.city || !address.state || !address.postalCode || !address.country) {
                this.addValidationError(`Billing Address: All fields are required.`, 'addresses');
                hasErrors = true;
              }
            }
          });
        }
      }
      
      return hasErrors;
    },
    getNumberOfPrimaryContacts() {
      const primaryExistingContacts = this.contacts.filter(c => c.primary).length;
      const primaryNewContacts = this.newContacts.filter(c => c.primary).length;
      return primaryExistingContacts + primaryNewContacts;
    },
    showError(title, message) {
      this.errorTitle = title;
      this.errorMessage = message;
      this.showErrorModal = true;
    },
    addValidationError(message, tab = null) {
      if (!this.validationErrors.includes(message)) {
        this.validationErrors.push(message);
        if (tab && !this.errorTabs.includes(tab)) {
          this.errorTabs.push(tab);
        }
      }
    },
    showAllErrors() {
      if (this.validationErrors.length > 0) {
        this.errorTitle = 'Validation Errors';
        this.errorMessage = this.validationErrors.join('\n\n');
        this.showErrorModal = true;
      }
    },
    clearValidationErrors() {
      this.validationErrors = [];
      this.errorTabs = [];
    },
    closeErrorModal() {
      this.showErrorModal = false;
      this.errorTitle = 'Validation Error';
      this.errorMessage = '';
      this.clearValidationErrors();
    },
    showSuccess(title, message, navigate = false) {
      this.successTitle = title;
      this.successMessage = message;
      this.navigateOnSuccess = navigate;
      this.successAskStay = !!navigate;
      this.showSuccessModal = true;
    },
    closeSuccessModal() {
      this.showSuccessModal = false;
      this.successTitle = 'Success';
      this.successMessage = '';
      this.successAskStay = false;
      this.navigateOnSuccess = false;
    },
    stayOnPageAfterSave() {
      this.closeSuccessModal();
    },
    leavePageAfterSave() {
      this.closeSuccessModal();
      this.$router.go(-1);
    },
    async loadVendorData() {
       try {
         const response = await ApiService.get(`/vendor/vendors/${this.vendorId}`);
         const vendor = response.data;
         
         // Helper function to format date from ISO to MM/DD/YYYY
         const formatDate = (dateString) => {
           if (!dateString) return this.getCurrentDate();
           try {
             const date = new Date(dateString);
             if (isNaN(date.getTime())) return this.getCurrentDate();
             const month = String(date.getMonth() + 1).padStart(2, '0');
             const day = String(date.getDate()).padStart(2, '0');
             const year = date.getFullYear();
             return `${month}/${day}/${year}`;
           } catch (error) {
             return this.getCurrentDate();
           }
         };
         
         this.form = {
           vendorId: vendor.vendorId || vendor.id || '',
           vendorName: vendor.vendorName || vendor.name || '',
           vendorRole: vendor.vendorRole || '',
           active: vendor.active !== false,
           vendorSince: formatDate(vendor.vendorSince),
           taxId: vendor.taxId || '',
           openingBalance: vendor.openingBalance || 0,
           balanceAsOf: formatDate(vendor.balanceAsOf),
           balance: vendor.balance || 0,
          phone: vendor.phone || '',
          fax: vendor.fax || '',
          email: vendor.email || '',
          webpage: vendor.webpage || '',
          notes: vendor.notes || '',
          accountId: vendor.accountId || '',
          creditLimit: vendor.creditLimit || 0,
          shippingMethodId: vendor.shippingMethodId || '',
          paymentMethod: vendor.paymentMethod || '',
          paymentTermId: vendor.paymentTermId || '',
          accountNumber: vendor.accountNumber || '',
          bankName: vendor.bankName || '',
          bankBranch: vendor.bankBranch || '',
          bankAccountType: vendor.bankAccountType || '',
          bankRoutingNumber: vendor.bankRoutingNumber || '',
          vendorGroupId: vendor.vendorGroupId || '',
          taxRegistrationNumber: vendor.taxRegistrationNumber || '',
          taxCode: vendor.taxCode || '',
          serviceTaxRegistrationNo: vendor.serviceTaxRegistrationNo || '',
          addressDetails: vendor.addressDetails || ''
        };
        
        this.contacts = vendor.contacts || [];
        this.addresses = vendor.addresses || [];
        this.selectedAddressType = vendor.address || '';
        
        // Handle documents - convert from backend format to frontend format
        this.documents = (vendor.documents || []).map(doc => ({
          id: doc.id,
          fileName: doc.fileName || '',
          originalFileName: doc.originalFileName || doc.fileName || '',
          filePath: doc.filePath || '',
          fileSize: doc.fileSize || 0,
          mimeType: doc.mimeType || '',
          uploadedAt: doc.uploadedAt || doc.createdAt || '',
          description: doc.description || '',
          vendorId: doc.vendorId,
          companyId: doc.companyId
        }));
        
        // Store the original vendor ID and email for validation
        this.originalVendorId = this.form.vendorId;
        this.originalVendorEmail = vendor.email || '';
        
        // Store original contact emails for validation
        this.contacts.forEach((contact, idx) => {
          if (contact.email) {
            this.originalContactEmails[`contact-${idx}`] = contact.email;
          }
        });
        
        // Reset document tracking arrays
        this.documentsToDelete = [];
        this.documentsToUpload = [];
        
        this.fillFromSelectedAddress();
      } catch (error) {
        logger.error('Error loading vendor data:', error);
        this.showError('Error Loading Vendor', 'Failed to load vendor data. Please try again.');
      } finally {
        this.loading = false;
      }
    },
    async handleSave() {
      this.showErrors = true;
      this.clearValidationErrors();
      
      // Validate vendor ID, vendor name and other fields
      const errors = [];
      if (!this.form.vendorId || this.form.vendorId.trim() === '') {
        errors.push('Vendor ID is required.');
      }
      // Only show vendor ID exists error if it's not the original vendor ID
      if (this.vendorIdExists && this.form.vendorId.trim() !== this.originalVendorId) {
        errors.push('This Vendor ID already exists in this company.');
      }
      if (!this.form.vendorName || this.form.vendorName.trim() === '') {
        errors.push('Vendor Name is required.');
      }
      if (!this.form.paymentTermId) {
        errors.push('Payment Terms is required.');
      }
      if (!this.form.paymentMethod) {
        errors.push('Payment Method is required.');
      }
      if (this.form.openingBalance < 0) {
        errors.push('Opening Balance cannot be negative.');
      }
      if (this.form.creditLimit < 0) {
        errors.push('Credit Limit cannot be negative.');
      }
      if (this.form.vendorSince && !this.validateDate('vendorSince')) {
        errors.push('Please enter a valid date in MM/DD/YYYY format for Vendor Since.');
      }
      if (this.form.balanceAsOf && !this.validateDate('balanceAsOf')) {
        errors.push('Please enter a valid date in MM/DD/YYYY format for Balance as of.');
      }
      if (this.form.accountNumber && !/^\d+$/.test(this.form.accountNumber)) {
        errors.push('Account Number must contain only digits.');
      }
      
      if (errors.length > 0) {
        this.showError('Validation Error', errors.join('\n\n'));
        return;
      }
      
      // Check for vendor email validation errors
      if (this.vendorEmailError) {
        this.showError('Email Validation Error', this.vendorEmailError);
        this.activeTab = 'general';
        return;
      }
       
       this.fillFromSelectedAddress();
       
       // Convert dates to proper format if needed
       const formatDateForBackend = (dateString) => {
         if (!dateString) return null;
         // If it's already in MM/DD/YYYY format, convert to ISO
         if (dateString.includes('/')) {
           const [month, day, year] = dateString.split('/');
           return new Date(year, month - 1, day).toISOString();
         }
         return dateString;
       };
       
       // Validate vendor email before saving (if email is being changed)
       if (this.form.email && this.form.email.trim()) {
         // Get original email
         const originalEmail = (this.originalVendorEmail || '').toLowerCase().trim();
         const newEmail = this.form.email.toLowerCase().trim();
         
         if (newEmail !== originalEmail) {
           const emailCheck = await vendorService.checkVendorEmailExists(this.form.email.trim(), this.companyId, this.vendorId);
           if (emailCheck.data.exists) {
             let errorMessage = '';
             if (emailCheck.data.existsAsConsultant) {
               errorMessage = 'This email address already exists for a consultant in this company.';
             } else if (emailCheck.data.existsAsContact) {
               errorMessage = 'This email address already exists for a customer contact in this company.';
             } else if (emailCheck.data.existsAsVendor) {
               errorMessage = 'This email address already exists for another vendor in this company.';
             } else {
               errorMessage = 'This email address is already in use.';
             }
             this.showError('Email Validation Error', errorMessage);
             return;
           }
         }
       }
       
       const payload = {
         vendorId: this.form.vendorId,
         vendorName: this.form.vendorName,
         vendorRole: this.form.vendorRole || null,
         active: this.form.active,
         vendorSince: formatDateForBackend(this.form.vendorSince),
         taxId: this.form.taxId,
         openingBalance: this.form.openingBalance,
         balanceAsOf: formatDateForBackend(this.form.balanceAsOf),
         balance: this.form.balance,
         phone: this.form.phone,
         fax: this.form.fax,
         email: this.form.email,
         webpage: this.form.webpage,
         notes: this.form.notes,
         accountId: this.form.accountId || null,
         creditLimit: this.form.creditLimit,
         shippingMethodId: this.form.shippingMethodId || null,
         paymentMethod: this.form.paymentMethod,
         paymentTermId: this.form.paymentTermId || null,
         accountNumber: this.form.accountNumber,
         bankName: this.form.bankName,
         bankBranch: this.form.bankBranch,
         bankAccountType: this.form.bankAccountType || null,
         bankRoutingNumber: this.form.bankRoutingNumber,
         vendorGroupId: this.form.vendorGroupId || null,
         taxRegistrationNumber: this.form.taxRegistrationNumber,
         taxCode: this.form.taxCode,
         serviceTaxRegistrationNo: this.form.serviceTaxRegistrationNo,
         companyId: this.companyId,
         contacts: [...this.contacts, ...this.newContacts],
         addresses: [...this.addresses, ...this.newAddresses],
         address: this.selectedAddressType,
         addressDetails: this.form.addressDetails
       };
       
       try {
        await ApiService.put(`/vendor/vendors/${this.vendorId}`, payload);
        
        // Handle document uploads and deletions
        await this.handleDocumentChanges(this.vendorId, this.companyId);
        
        this.showSuccess(
          'Success',
          'The vendor has been updated successfully.\nStay on this page to view the updated details?',
          true
        );
        
        // Refresh the vendor data to show the updated information
        await this.loadVendorData();
       } catch (error) {
         let errorMessage = error.response?.data?.error || 'Failed to update vendor. Please try again.';
         
         // Handle email-related errors
         if (errorMessage.includes('email') || errorMessage.includes('Email')) {
           if (errorMessage.includes('consultant')) {
             this.showError('Email Validation Error', 'This email address already exists for a consultant in this company.');
           } else if (errorMessage.includes('customer contact') || errorMessage.includes('contact')) {
             this.showError('Email Validation Error', 'This email address already exists for a customer contact in this company.');
           } else if (errorMessage.includes('vendor')) {
             this.showError('Email Validation Error', 'This email address already exists for another vendor in this company.');
           } else {
             this.showError('Email Validation Error', errorMessage);
           }
           return;
         }
         
         this.showError('Update Failed', errorMessage);
       }
     },
    handleCancel() {
      this.$router.go(-1);
    },
    handleVendorSinceChange(date) {
      this.form.vendorSince = date;
    },
    handleBalanceAsOfChange(date) {
      this.form.balanceAsOf = date;
    },
    fillFromSelectedAddress() {
      const addr = this.addresses.find(a => a.type === this.selectedAddressType);
      if (addr) {
        this.form.addressDetails = [
          addr.address1,
          addr.address2,
          addr.city,
          addr.state,
          addr.postalCode,
          addr.country
        ].filter(Boolean).join('\n');
      } else {
        this.form.addressDetails = '';
      }
    },
    autoResizeAddressTextarea() {
      this.$nextTick(() => {
        const textarea = this.$refs.addressDetailsTextarea;
        if (textarea) {
          textarea.style.height = 'auto';
          textarea.style.height = textarea.scrollHeight + 'px';
        }
      });
    },
    getAvailableAddressTypes(idx, isNew) {
      const allTypes = ["Billing", "Shipping", "Other"];
      let usedTypes = this.addresses.map((a, i) => (isNew ? a.type : (i !== idx ? a.type : null))).filter(Boolean);
      if (isNew) {
        usedTypes = usedTypes.concat(this.newAddresses.map((a, i) => (i !== idx ? a.type : null)).filter(Boolean));
      }
      let currentType = isNew ? this.newAddresses[idx]?.type : this.addresses[idx]?.type;
      return allTypes.filter(type => !usedTypes.includes(type) || type === currentType);
    },
    addContactRow() {
      if (this.newContacts.length === 0) {
        this.newContacts.push({
          primary: false,
          name: '',
          title: '',
          phone: '',
          email: ''
        });
      } else {
        this.showError('Cannot Add Contact', 'Please save or cancel the current new contact before adding another.\n\nYou can only add one contact at a time to ensure proper data entry and validation.');
      }
    },
    async saveContact(nidx) {
      const contact = this.newContacts[nidx];
      
      // Validate all required fields
      const errors = [];
      if (!contact.name || !contact.name.trim()) {
        errors.push('Contact Name is required');
      }
      if (!contact.title || !contact.title.trim()) {
        errors.push('Title is required');
      }
      if (!contact.phone || !contact.phone.trim()) {
        errors.push('Business Phone is required');
      } else if (!/^[\d\s-+()]+$/.test(contact.phone)) {
        errors.push('Invalid phone number format (allowed: digits, spaces, -, +, parentheses)');
      }
      if (!contact.email || !contact.email.trim()) {
        errors.push('Email is required');
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) {
        errors.push('Invalid email format');
      }

      if (errors.length > 0) {
        this.showError('Contact Validation Error', errors.join('\n\n'));
        return;
      }
      
      // Validate email uniqueness (immediate check, no debounce)
      await this.validateContactEmail(contact.email, nidx, true, true);
      if (this.contactEmailErrors[`new-contact-${nidx}`]) {
        this.errorTitle = 'Email Validation Error';
        this.errorMessage = this.contactEmailErrors[`new-contact-${nidx}`];
        this.showErrorModal = true;
        return;
      }
      
      // Handle primary contact logic
      if (this.contacts.length === 0) {
        // First contact must be primary
        contact.primary = true;
      } else if (contact.primary) {
        // If this contact is being saved as primary, uncheck all others
        this.contacts.forEach(c => c.primary = false);
        this.newContacts.forEach((c, i) => {
          if (i !== nidx) c.primary = false;
        });
      } else if (this.getNumberOfPrimaryContacts() === 0) {
        // If no primary contacts exist, force this one to be primary
        contact.primary = true;
      }
      
      const newIdx = this.contacts.length;
      this.contacts.push(contact);
      this.newContacts.splice(nidx, 1);
      
      // Store the original email for this contact
      if (contact.email) {
        this.originalContactEmails[`contact-${newIdx}`] = contact.email;
      }
      
      // Clear the error for the new contact since it's now saved
      delete this.contactEmailErrors[`new-contact-${nidx}`];
      
      // Update phone and email if this is the primary contact
      if (contact.primary) {
        this.form.phone = contact.phone || '';
        this.form.email = contact.email || '';
      }
      
      this.showSuccess('Contact Added', 'Contact added successfully.');
    },
    cancelContact(nidx) {
      this.newContacts.splice(nidx, 1);
    },
    editContact(idx) {
      this.editingContactIdx = idx;
      // Store the original email when starting to edit
      const contact = this.contacts[idx];
      if (contact && contact.email) {
        this.originalContactEmails[`contact-${idx}`] = contact.email;
      }
    },
    async updateContact() {
      const contact = this.contacts[this.editingContactIdx];
      
      // Validate all required fields
      const errors = [];
      if (!contact.name || !contact.name.trim()) {
        errors.push('Contact Name is required');
      }
      if (!contact.title || !contact.title.trim()) {
        errors.push('Title is required');
      }
      if (!contact.phone || !contact.phone.trim()) {
        errors.push('Business Phone is required');
      } else if (!/^[\d\s-+()]+$/.test(contact.phone)) {
        errors.push('Invalid phone number format (allowed: digits, spaces, -, +, parentheses)');
      }
      if (!contact.email || !contact.email.trim()) {
        errors.push('Email is required');
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) {
        errors.push('Invalid email format');
      }

      if (errors.length > 0) {
        this.showError('Contact Validation Error', errors.join('\n\n'));
        return;
      }
      
      // Validate email uniqueness (immediate check, no debounce)
      await this.validateContactEmail(contact.email, this.editingContactIdx, false, true);
      if (this.contactEmailErrors[`contact-${this.editingContactIdx}`]) {
        this.errorTitle = 'Email Validation Error';
        this.errorMessage = this.contactEmailErrors[`contact-${this.editingContactIdx}`];
        this.showErrorModal = true;
        return;
      }
      
      // Handle primary contact logic
      if (contact.primary) {
        // If this contact is being set as primary, uncheck all others
        this.contacts.forEach((c, i) => {
          if (i !== this.editingContactIdx) c.primary = false;
        });
        this.newContacts.forEach(c => c.primary = false);
      } else if (this.getNumberOfPrimaryContacts() === 1) {
        // If this was the only primary contact, we can't uncheck it
        contact.primary = true;
        this.showError('Primary Contact Required', 'At least one contact must be marked as primary. This contact will remain as the primary contact.');
        return;
      }
      
      // Update phone and email if this is the primary contact
      if (contact.primary) {
        this.form.phone = contact.phone || '';
        this.form.email = contact.email || '';
      }
      
      // Store the original email for this contact
      this.originalContactEmails[`contact-${this.editingContactIdx}`] = contact.email;
      
      this.editingContactIdx = null;
      this.showSuccess('Contact Updated', 'Contact updated successfully.');
    },
    cancelEditContact() {
      this.editingContactIdx = null;
    },
    deleteContact(idx) {
      const contact = this.contacts[idx];
      const wasPrimary = contact && contact.primary;
      
      // If deleting the primary contact, make another contact primary
      if (wasPrimary && this.contacts.length > 1) {
        // Find another contact to make primary
        const otherContact = this.contacts.find((c, i) => i !== idx);
        if (otherContact) {
          otherContact.primary = true;
          this.form.phone = otherContact.phone || '';
          this.form.email = otherContact.email || '';
        }
      }
      
      this.contacts.splice(idx, 1);
      this.showSuccess('Contact Deleted', 'Contact deleted successfully.');
    },
    setPrimaryContact(idx) {
      const contact = this.contacts[idx];
      
      // If unchecking the only primary contact, prevent it
      if (!contact.primary && this.getNumberOfPrimaryContacts() === 1) {
        contact.primary = true;
        return;
      }
      
      // If checking this contact as primary, uncheck all others
      if (contact.primary) {
        this.newContacts.forEach(c => c.primary = false);
        this.contacts.forEach((c, i) => {
          if (i !== idx) c.primary = false;
        });
      }
      
      this.fillFromContact(idx);
    },
    fillFromContact(idx) {
      const contact = this.contacts[idx];
      if (contact && contact.primary) {
        this.form.phone = contact.phone || '';
        this.form.email = contact.email || '';
      }
    },
    setPrimaryContactNew(nidx) {
      const contact = this.newContacts[nidx];
      
      // If unchecking the only primary contact, prevent it
      if (!contact.primary && this.getNumberOfPrimaryContacts() === 1) {
        contact.primary = true;
        return;
      }
      
      // If checking this contact as primary, uncheck all others
      if (contact.primary) {
        this.contacts.forEach(c => c.primary = false);
        this.newContacts.forEach((c, i) => {
          if (i !== nidx) c.primary = false;
        });
      }
    },
    addNewAddressRow() {
      if (this.newAddresses.length === 0) {
      this.newAddresses.push({ 
        type: '', 
        address1: '', 
        address2: '', 
        city: '', 
        state: '', 
        postalCode: '', 
        country: '' 
      });
      } else {
        this.showError('Cannot Add Address', 'Please save or cancel the current new address before adding another.\n\nYou can only add one address at a time to ensure proper data entry and validation.');
      }
    },
    saveNewAddress(idx) {
      const na = this.newAddresses[idx];
      
      // Validate all required fields
      const errors = [];
      if (!na.type) {
        errors.push('Address Type is required');
      }
      if (!na.address1 || !na.address1.trim()) {
        errors.push('Address Line 1 is required');
      }
      if (!na.city || !na.city.trim()) {
        errors.push('City is required');
      }
      if (!na.state || !na.state.trim()) {
        errors.push('State is required');
      }
      if (!na.postalCode || !na.postalCode.trim()) {
        errors.push('Postal Code is required');
      }
      if (!na.country || !na.country.trim()) {
        errors.push('Country is required');
      }

      if (errors.length > 0) {
        this.showError('Address Validation Error', errors.join('\n\n'));
        return;
      }
      this.addresses = [...this.addresses, na];
      this.newAddresses.splice(idx, 1);
      this.selectedAddressType = na.type;
      this.fillFromSelectedAddress();
      this.$nextTick(() => {
        this.autoResizeAddressTextarea();
      });
    },
    cancelNewAddress(idx) {
      this.newAddresses.splice(idx, 1);
    },
    editAddress(idx) {
      this.editingAddressIdx = idx;
    },
    updateAddress() {
      const address = this.addresses[this.editingAddressIdx];
      
      // Validate all required fields
      const errors = [];
      if (!address.type) {
        errors.push('Address Type is required');
      }
      if (!address.address1 || !address.address1.trim()) {
        errors.push('Address Line 1 is required');
      }
      if (!address.city || !address.city.trim()) {
        errors.push('City is required');
      }
      if (!address.state || !address.state.trim()) {
        errors.push('State is required');
      }
      if (!address.postalCode || !address.postalCode.trim()) {
        errors.push('Postal Code is required');
      }
      if (!address.country || !address.country.trim()) {
        errors.push('Country is required');
      }

      if (errors.length > 0) {
        this.showError('Address Validation Error', errors.join('\n\n'));
        return;
      }

      this.editingAddressIdx = null;
      
      // Update the address details if this is the selected address type
      if (this.selectedAddressType === address.type) {
        this.fillFromSelectedAddress();
      }
      
      this.showSuccess('Address Updated', 'Address updated successfully.');
    },
    cancelEditAddress() {
      this.editingAddressIdx = null;
    },
    deleteAddress(idx) {
      this.addresses.splice(idx, 1);
    },
    openCustomFieldsModal() {
      this.showCustomFieldsModal = true;
    },
    closeCustomFieldsModal() {
      this.showCustomFieldsModal = false;
    },
    handleAccountChange(eventOrValue) {
      const selected = this.resolveSelectValue(eventOrValue);
      if (selected === '__add_new__') {
        this.navigateToNewAccount();
      }
    },
    navigateToNewAccount() {
      this.$router.push(`/company/${this.companyId}/dashboard/new-account`);
    },
    async fetchVendorGroups() {
      try {
        const groups = await CompanyService.getAllVendorGroups(this.companyId);
        this.vendorGroups = Array.isArray(groups) ? groups : [];
      } catch (err) {
        this.vendorGroups = [];
      }
    },
    handleVendorGroupChange(eventOrValue) {
      const selected = this.resolveSelectValue(eventOrValue);
      if (selected === '__create_new__') {
        this.showAddGroupModal = true;
        this.$nextTick(() => {
          this.form.vendorGroupId = this.previousVendorGroup;
        });
      } else {
        this.form.vendorGroupId = selected;
        this.previousVendorGroup = selected;
      }
    },
    async onAddGroupOk(group) {
      this.showAddGroupModal = false;
      await this.fetchVendorGroups();
      if (group && group.id) {
        this.form.vendorGroupId = group.id;
        this.previousVendorGroup = group.id;
        this.showSuccess('Vendor Group Created', 'Vendor group created successfully!');
      }
    },
    handlePaymentTermChange(eventOrValue) {
      const selected = this.resolveSelectValue(eventOrValue);
      if (selected === '__add_new__') {
        this.showAddPaymentTermModal = true;
        this.$nextTick(() => {
          this.form.paymentTermId = this.previousPaymentTerm;
        });
      } else {
        this.form.paymentTermId = selected;
        this.previousPaymentTerm = selected;
      }
    },
    async onAddPaymentTermOk(newTerm) {
      this.showAddPaymentTermModal = false;
      await this.fetchPaymentTerms();
      if (newTerm && newTerm.id) {
        this.form.paymentTermId = newTerm.id;
        this.previousPaymentTerm = newTerm.id;
        this.showSuccess('Payment Term Created', 'Payment term created successfully!');
      }
    },
    async fetchPaymentTerms() {
      try {
        const termsRes = await ApiService.get(`/company/payment-terms?companyId=${this.companyId}`);
        this.paymentTermsList = Array.isArray(termsRes.data.paymentTerms) ? termsRes.data.paymentTerms : [];
      } catch (err) {
        this.paymentTermsList = [];
      }
    },
    handleShippingMethodChange(eventOrValue) {
      const selected = this.resolveSelectValue(eventOrValue);
      if (selected === '__add_new__') {
        this.showAddShippingMethodModal = true;
        this.$nextTick(() => {
          this.form.shippingMethodId = this.previousShippingMethod;
        });
      } else {
        this.form.shippingMethodId = selected;
        this.previousShippingMethod = selected;
      }
    },
    async onAddShippingMethodOk(newMethod) {
      this.showAddShippingMethodModal = false;
      await this.fetchShippingMethods();
      if (newMethod && newMethod.id) {
        this.form.shippingMethodId = newMethod.id;
        this.previousShippingMethod = newMethod.id;
        this.showSuccess('Shipping Method Created', 'Shipping method created successfully!');
      }
    },
    async fetchShippingMethods() {
      try {
        const res = await ApiService.get(`/vendor/preferred-shipping-methods?companyId=${this.companyId}`);
        this.shippingMethodsList = Array.isArray(res.data) ? res.data : [];
      } catch (err) {
        this.shippingMethodsList = [];
      }
    },
    async fetchAccounts() {
      try {
        const res = await ApiService.getCompanyAccounts(this.companyId);
        const allAccounts = res.data.accounts || res.data || [];
        const allowedTypes = [
          'Expense', 'Other Expense', 'Cost of Goods Sold', 'Cash', 'Bank', 'Credit Card', 'Current Liability', 'Paypal'
        ];
        this.accounts = allAccounts;
        this.filteredAccounts = allAccounts.filter(a => allowedTypes.includes(a.accountType));
      } catch (err) {
        this.accounts = [];
        this.filteredAccounts = [];
      }
    },
    handleContactEmailInput(email, idx, isNew) {
      const key = isNew ? `new-contact-${idx}` : `contact-${idx}`;
      this.clearContactEmailError(key);
      
      if (!email || !email.trim()) {
        return;
      }
      
      // Clear previous timeout
      if (this.contactEmailCheckTimeouts[key]) {
        clearTimeout(this.contactEmailCheckTimeouts[key]);
      }
      
      // Debounce the validation
      this.contactEmailCheckTimeouts[key] = setTimeout(() => {
        this.validateContactEmail(email, idx, isNew);
      }, 500);
    },
    async validateContactEmail(email, idx, isNew, immediate = false) {
      if (!email || !email.trim()) {
        return;
      }
      
      // Basic email format validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email.trim())) {
        const key = isNew ? `new-contact-${idx}` : `contact-${idx}`;
        this.contactEmailErrors[key] = 'Please enter a valid email address.';
        this.checkingContactEmails[key] = false;
        return;
      }
      
      const key = isNew ? `new-contact-${idx}` : `contact-${idx}`;
      const companyId = this.companyId;
      
      if (!companyId) {
        this.checkingContactEmails[key] = false;
        return;
      }
      
      // Check if email is being changed (for existing contacts)
      if (!isNew) {
        const originalEmail = (this.originalContactEmails[key] || '').toLowerCase().trim();
        const newEmail = email.toLowerCase().trim();
        
        if (newEmail === originalEmail) {
          // Email hasn't changed, no need to check
          delete this.contactEmailErrors[key];
          this.checkingContactEmails[key] = false;
          return;
        }
      }
      
      // Clear previous timeout
      if (this.contactEmailCheckTimeouts[key]) {
        clearTimeout(this.contactEmailCheckTimeouts[key]);
      }
      
      // If immediate, check right away; otherwise debounce
      const performCheck = async () => {
        this.checkingContactEmails[key] = true;
        delete this.contactEmailErrors[key];
        
        try {
          const response = await vendorService.checkVendorEmailExists(email.trim(), companyId, this.vendorId);
          
          if (response.data.exists) {
            let errorMessage = '';
            if (response.data.existsAsConsultant) {
              errorMessage = 'This email address already exists for a consultant in this company.';
            } else if (response.data.existsAsContact) {
              errorMessage = 'This email address already exists for a customer contact in this company.';
            } else if (response.data.existsAsVendor) {
              errorMessage = 'This email address already exists for another vendor or vendor contact in this company.';
            } else {
              errorMessage = 'This email address is already in use.';
            }
            this.contactEmailErrors[key] = errorMessage;
          }
        } catch (error) {
          logger.error('Error checking contact email:', error);
          // Don't show error to user for validation check failures
        } finally {
          this.checkingContactEmails[key] = false;
        }
      };
      
      if (immediate) {
        await performCheck();
      } else {
        this.contactEmailCheckTimeouts[key] = setTimeout(performCheck, 500);
      }
    },
    clearContactEmailError(key) {
      delete this.contactEmailErrors[key];
      if (this.contactEmailCheckTimeouts[key]) {
        clearTimeout(this.contactEmailCheckTimeouts[key]);
        delete this.contactEmailCheckTimeouts[key];
      }
      this.checkingContactEmails[key] = false;
    },
    // Document handling methods
    handleDocumentFileUpload(event) {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      const fileArray = Array.from(files);
      let processedCount = 0;
      const totalFiles = fileArray.length;

      fileArray.forEach(file => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const getMimeType = (filename, fallbackType) => {
            if (fallbackType && fallbackType !== '') return fallbackType;
            const extension = filename.split('.').pop().toLowerCase();
            const mimeMap = {
              'pdf': 'application/pdf',
              'doc': 'application/msword',
              'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
              'xls': 'application/vnd.ms-excel',
              'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              'jpg': 'image/jpeg',
              'jpeg': 'image/jpeg',
              'png': 'image/png',
              'gif': 'image/gif',
              'txt': 'text/plain'
            };
            return mimeMap[extension] || 'application/octet-stream';
          };

          const document = {
            fileName: file.name,
            originalFileName: file.name,
            fileSize: file.size,
            filePath: e.target.result,
            mimeType: getMimeType(file.name, file.type),
            uploadedAt: new Date().toISOString(),
            file: file // Store the actual file for upload
          };
          this.documents.push(document);
          processedCount++;
          
          // Auto-select the last document when all files are processed
          if (processedCount === totalFiles) {
            this.$nextTick(async () => {
              this.selectedDocumentIndex = this.documents.length - 1;
              await this.selectDocument(this.selectedDocumentIndex);
            });
          } else if (this.selectedDocumentIndex === null && processedCount === 1) {
            // Select first document if none selected
            this.$nextTick(async () => {
              this.selectedDocumentIndex = 0;
              await this.selectDocument(0);
            });
          }
        };
        reader.readAsDataURL(file);
      });

      if (this.$refs.addDocumentFileInput) {
        this.$refs.addDocumentFileInput.value = '';
      }
    },
    removeVendorDocument(index) {
      if (this.documents && this.documents.length > index) {
        const document = this.documents[index];
        
        // Store the document index to delete and show warning modal
        this.documentToDelete = index;
        const fileName = document.originalFileName || document.fileName || 'this document';
        this.showWarning('Delete Document', `Are you sure you want to delete "${fileName}"? This action cannot be undone.`);
      }
    },
    
    confirmDocumentDeletion(index) {
      if (!this.documents || this.documents.length <= index) return;
      
      const document = this.documents[index];
      
      // If document has an ID, it's an existing document that needs to be deleted
      if (document.id) {
        this.documentsToDelete.push(document.id);
      }
      
      // Remove preview URL from cache and revoke blob URL if needed
      if (document && document.filePath) {
        const cachedUrl = this.documentPreviewUrls[document.filePath];
        if (cachedUrl && cachedUrl.startsWith('blob:')) {
          window.URL.revokeObjectURL(cachedUrl);
        }
        delete this.documentPreviewUrls[document.filePath];
      }
      
      // If removing the selected document, clear selection and preview
      if (this.selectedDocumentIndex === index) {
        this.selectedDocumentIndex = null;
        this.currentPreviewUrl = '';
        this.previewError = false;
      } else if (this.selectedDocumentIndex > index) {
        // Adjust selected index if document before it was removed
        this.selectedDocumentIndex--;
      }
      
      this.documents.splice(index, 1);
      this.showSuccess('Document Deleted', 'Document has been marked for deletion and will be removed when you save changes.');
    },
    async selectDocument(index) {
      this.selectedDocumentIndex = index;
      this.previewError = false;
      this.currentPreviewUrl = '';
      this.loadingPreview = true;
      
      const document = this.documents[index];
      if (!document) {
        this.loadingPreview = false;
        this.previewError = true;
        return;
      }
      
      // Check if URL is already cached
      if (this.documentPreviewUrls[document.filePath]) {
        this.currentPreviewUrl = this.documentPreviewUrls[document.filePath];
        this.loadingPreview = false;
        return;
      }
      
      // Load preview URL
      try {
        const url = await this.getDocumentPreviewUrl(document);
        if (url) {
          this.currentPreviewUrl = url;
          this.previewError = false;
        } else {
          this.previewError = true;
        }
      } catch (error) {
        logger.error('Error loading preview:', error);
        this.previewError = true;
        this.currentPreviewUrl = '';
      } finally {
        this.loadingPreview = false;
      }
    },
    handlePreviewError() {
      this.previewError = true;
      this.currentPreviewUrl = '';
    },
    async getDocumentPreviewUrl(document) {
      if (!document || !document.filePath) return '';
      
      // If already cached, return cached URL
      if (this.documentPreviewUrls[document.filePath]) {
        return this.documentPreviewUrls[document.filePath];
      }
      
      // If it's a new document (data URL), use it directly
      if (document.filePath.startsWith('data:')) {
        this.documentPreviewUrls[document.filePath] = document.filePath;
        return document.filePath;
      }
      
      // For saved documents, fetch from backend
      if (document.id) {
        try {
          const companyId = this.companyId;
          if (!companyId) {
            logger.error('Company ID not found');
            return '';
          }
          
          // Fetch the document with blob response type
          const response = await vendorService.viewVendorDocument(document.id, companyId);
          
          // Check if response data exists
          if (!response || !response.data) {
            logger.error('No data in response');
            return '';
          }
          
          // Determine MIME type from response headers or use document mimeType
          const contentType = response.headers && response.headers['content-type'];
          const mimeType = contentType || 
                          document.mimeType || 
                          'application/pdf';
          
          // Create blob from response data
          const blob = response.data instanceof Blob 
            ? response.data 
            : new Blob([response.data], { type: mimeType });
          
          const url = window.URL.createObjectURL(blob);
          
          // Cache the URL
          this.documentPreviewUrls[document.filePath] = url;
          return url;
        } catch (error) {
          logger.error('Failed to load document preview:', error);
          return '';
        }
      }
      
      return '';
    },
    isImageFile(document) {
      if (!document || !document.mimeType) return false;
      const imageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      return imageTypes.includes(document.mimeType.toLowerCase());
    },
    isPdfFile(document) {
      if (!document || !document.mimeType) return false;
      return document.mimeType.toLowerCase() === 'application/pdf';
    },
    async openDocumentInNewWindow(document) {
      const url = await this.getDocumentPreviewUrl(document);
      if (url) {
        window.open(url, '_blank');
      } else {
        this.showError('Error', 'Failed to open document');
      }
    },
    async handleDocumentChanges(vendorId, companyId) {
      // Delete documents that were removed
      if (this.documentsToDelete.length > 0) {
        const deletionErrors = [];
        for (const docId of this.documentsToDelete) {
          try {
            await vendorService.deleteVendorDocument(docId, companyId);
          } catch (error) {
            logger.error('Error deleting document:', error);
            const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
            deletionErrors.push(`Document ID ${docId}: ${errorMessage}`);
          }
        }
        
        // Show error if any deletions failed
        if (deletionErrors.length > 0) {
          this.showError('Document Deletion Error', `Failed to delete some documents:\n${deletionErrors.join('\n')}`);
        }
        
        this.documentsToDelete = [];
      }
      
      // Upload new documents
      const documentsToUpload = this.documents.filter(doc => doc.file && !doc.id);
      if (documentsToUpload.length > 0) {
        try {
          const formData = new FormData();
          formData.append('vendorId', vendorId);
          formData.append('companyId', companyId);
          
          documentsToUpload.forEach((doc) => {
            if (doc.file) {
              formData.append('documents', doc.file);
            }
          });
          
          const response = await vendorService.uploadVendorDocuments(vendorId, companyId, formData);
          logger.log('Documents uploaded successfully', response.data);
          
          // Update documents array with uploaded document IDs
          if (response.data && response.data.data && Array.isArray(response.data.data)) {
            const uploadedDocs = response.data.data;
            documentsToUpload.forEach((doc, index) => {
              if (uploadedDocs[index]) {
                const uploadedDoc = uploadedDocs[index];
                // Find the document in the array and update it
                const docIndex = this.documents.findIndex(d => d === doc);
                if (docIndex !== -1) {
                  this.documents[docIndex].id = uploadedDoc.id;
                  this.documents[docIndex].vendorId = uploadedDoc.vendorId;
                  this.documents[docIndex].uploadedAt = uploadedDoc.uploadedAt;
                  this.documents[docIndex].filePath = uploadedDoc.filePath;
                  delete this.documents[docIndex].file; // Remove file reference
                }
              }
            });
          }
        } catch (error) {
          logger.error('Error uploading documents:', error);
          this.showError('Warning', 'Vendor updated but some documents failed to upload: ' + (error.message || 'Please try uploading documents again'));
        }
      }
    },
    formatFileSize(bytes) {
      if (!bytes || bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    },
    showWarning(title, message, confirmButtonText = 'Delete') {
      this.warningTitle = title;
      this.warningMessage = message;
      this.warningConfirmButtonText = confirmButtonText;
      this.showWarningModal = true;
    },
    closeWarningModal() {
      this.showWarningModal = false;
      this.warningTitle = 'Warning';
      this.warningMessage = '';
      this.warningConfirmButtonText = 'Delete';
      this.documentToDelete = null;
    },
    confirmDelete() {
      // Handle document deletion
      if (this.documentToDelete !== null) {
        this.confirmDocumentDeletion(this.documentToDelete);
      }
      
      this.closeWarningModal();
    }
  },
  beforeUnmount() {
    // Cleanup blob URLs to prevent memory leaks
    Object.values(this.documentPreviewUrls).forEach(url => {
      if (url && url.startsWith('blob:')) {
        window.URL.revokeObjectURL(url);
      }
    });
  },
  watch: {
    contacts: {
      handler(newContacts) {
        const primary = newContacts.find(c => c.primary);
        if (primary) {
          this.form.phone = primary.phone || '';
          this.form.email = primary.email || '';
        } else {
          this.form.phone = '';
          this.form.email = '';
        }
      },
      deep: true
    },
    selectedAddressType() {
      this.fillFromSelectedAddress();
    }
  },
  async created() {
    this.vendorId = this.$route.params.vendorId;
    this.companyId = this.$route.params.id;
    
    if (!this.vendorId || !this.companyId) {
      this.$toast && this.$toast.error('Invalid vendor or company ID');
      this.$router.go(-1);
      return;
    }
    
    await Promise.all([
      this.loadVendorData(),
      this.fetchAccounts(),
      this.fetchPaymentTerms(),
      this.fetchVendorGroups(),
      this.fetchShippingMethods()
    ]);
  }
};
</script>

<style src="@/components/ManageCustomFields.css"></style>

